import * as React from "react";
import { cloneDeep, isEqual } from "lodash";
import { DragAndDropReordering } from "@app/shared/drag-and-drop-reordering";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";
import { QesIcon } from '@app/shared/icons/QESIcon';
import { QesIcons } from '@app/shared/icons/qesIcons';
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";

interface ManageOptionsProps {
  /**
   * An optional prop that displays any additional elements that should be displayed at a per-option level
   */
  buildAdditionalIndividualProperty?: (option: AccessReportsWizardOption) => JSX.Element;
  /**
   * An optional prop that displays any additional elements that should be handled in this component
   */
  additionalProperties?: JSX.Element;
  /**
   * An optional prop that hides the input that allows for new options to be created
   */
  disableAddingNewOptions?: boolean;
  /**
   * An optional prop that hides the button that allows for an option to be deleted
   */
  disableDeletion?: boolean;
  /**
   * An optional prop that hides the button that allows for an option's name to be edited
   */
  disableNameEdit?: boolean;
  /**
   * An optional prop that hides the button that allows for drag and drop reordering of the options
   */
  disableReordering?: boolean;
  /**
   * Prevents the user from making modifications to the template
   */
  disableUserActions: boolean;
  /**
   * An optional prop that displays an alert if an option cannot be deleted due to connected options
   */
  getOptionConnections?: (optionId: number) => JSX.Element;
  /**
   * Handles saving any modifications done to the list of options
   */
  onSaveOptionsClick: (options: AccessReportsWizardOption[]) => void;
  /**
   * The list of options to be managed
   */
  options: AccessReportsWizardOption[];
  /**
   * The option type in order to accurately describe errors
   */
  optionType: string;
  /**
   * An optional prop that disables the save button if at least one option is not present
   */
  requireMinimumOne?: boolean;
  /**
   * An optional prop that overrides the check to determine the disabling of the save button;
   * This is useful in case the optional "additional properties" prop alters the options in any way
   */
  savedOptions?: AccessReportsWizardOption[];
}

const ManageOptions: React.FC<ManageOptionsProps> = (props: ManageOptionsProps) => {
  const [connectedOptionsWarningElement, setConnectedOptionsWarningElement] = React.useState<JSX.Element>(null);
  const [connectedOptionsWarningOptionId, setConnectedOptionsWarningOptionId] = React.useState<number>(null);
  const [draggable, setDraggable] = React.useState(false);
  const [newOptionName, setNewOptionName] = React.useState("");
  const [optionNameBeingEdited, setOptionNameBeingEdited] = React.useState<number>(null);
  const [optionNameBeingEditedValue, setOptionNameBeingEditedValue] = React.useState("");
  const [options, setOptions] = React.useState<AccessReportsWizardOption[]>([]);

  React.useEffect(() => {
    if (props.options.length) {
      initializeOptions();
    }
  }, [props.options]);

  const onDragAndDropChange = (items: JSX.Element[]): void => {
    // re-order the options based on the returned elements
    const newOptions: AccessReportsWizardOption[] = [];

    for (const item of items) {
      // extract the option id from the element's id
      const optionId = item.props.id.replace(/\D/g, "");
      const option = options.find(x => x.id === parseInt(optionId));

      newOptions.push(option);
    }

    setOptions(newOptions);
  }

  const onDragClick = () => {
    setDraggable(true);
  }

  const onDragEnd = (): void => {
    setDraggable(false);
  }

  const getAddOptionSection = (): JSX.Element => {
    const addOptionValidationText: string = getAddOptionValidationText();

    return (
      <div className="row">
        <div className="col-12">
          <QESTextInput
            inputAppend={(
              <QESButton
                qesType="secondary"
                className="p-1"
                disabled={!getAddOptionButtonValidity()}
                qesTestId="access-reports-admin__manage-options--add-option"
                onClick={() => onAddOptionClick()}
              >
                <QesIcon iconName={QesIcons.Plus} description="Add" className="mb2"/>
              </QESButton>
            )}
            testId="access-reports-admin__manage-options--add-option-input"
            errorText={addOptionValidationText}
            disabled={props.disableUserActions}
            isValid={!addOptionValidationText}
            label={props.optionType}
            onChange={(e) => onOptionNameInputChange(e)}
            unrequiredValidation={true}
            value={newOptionName}
          />

          <hr />
        </div>
      </div>
    );
  }

  const getAddOptionButtonValidity = (): boolean => {
    const newOptions: string[] = options.map(x => x.displayName ? x.displayName.toUpperCase() : x.name.toUpperCase());

    if (!props.disableUserActions &&
      !!newOptionName &&
      !newOptions.includes(newOptionName.toUpperCase())
    ) {
      return true;
    }
  }

  const getAddOptionValidationText = (): string => {
    const newOptions: string[] = options.map(x => x.displayName ? x.displayName.toUpperCase() : x.name.toUpperCase());

    if (newOptions.includes(newOptionName.toUpperCase())) {
      return `A ${props.optionType} with that name already exists`;
    }
  }

  const getOptionNameBeingEditedValidationText = (): string => {
    if (!optionNameBeingEditedValue) {
      return `Please enter a ${props.optionType} name`;
    } else {
      const optionWithThisName: AccessReportsWizardOption = options.find(x => x.name === optionNameBeingEditedValue);

      if (!!optionWithThisName && optionNameBeingEdited !== optionWithThisName.id) {
        return `A ${props.optionType} with that name already exists`;
      } else {
        return "";
      }
    }
  }

  const getOptionsList = (): JSX.Element => {
    const elements: JSX.Element[] = options.map((option, optionIndex) => {
      const optionNameBeingEditedValidationText: string = (optionNameBeingEdited === option.id) && getOptionNameBeingEditedValidationText();

      // each option must have a unique id for the drag and drop reordering to work correctly
      return (
        <div id={`access-reports-admin__manage-options--option-container-${option.id}`} key={optionIndex}>
          <QESTextInput
            inputAppend={(
              <React.Fragment>
                {optionNameBeingEdited === option.id ? (
                  <QESButton
                    className="p-1"
                    disabled={!!optionNameBeingEditedValidationText}
                    qesTestId={`access-reports-admin__manage-options--save-name-${option.id}`}
                    qesType="secondary"
                    onClick={() => onSaveOptionNameClick(option.id)}
                    title="Save"
                  >
                    <QesIcon iconName={QesIcons.Check} description="Save"/>
                  </QESButton>
                ) : (
                  <React.Fragment>
                    {!props.disableNameEdit && (
                      <QESButton
                        qesType="secondary"
                        className="p-1"
                        disabled={props.disableUserActions}
                        qesTestId={`access-reports-admin__manage-options--edit-name-${option.id}`}
                        onClick={() => !props.disableUserActions && onEditOptionNameClick(option)}
                        title="Edit Name"
                      >
                          <QesIcon iconName={QesIcons.PencilV1} description="Edit" />
                      </QESButton>
                    )}

                    {!props.disableReordering && (
                      <QESButton
                        qesType="secondary"
                        className="p-1"
                        disabled={props.disableUserActions}
                        qesTestId={`access-reports-admin__manage-options--drag-and-drop-${option.id}`}
                        onMouseDown={() => !props.disableUserActions && onDragClick()}
                        title="Reorder"
                      >
                          <QesIcon iconName={QesIcons.DragAndDropTexture} description="Move" />
                      </QESButton>
                    )}

                    {!props.disableDeletion && (
                      <QESButton
                        className="p-1"
                        disabled={props.disableUserActions}
                        qesTestId={`access-reports-admin__manage-options--delete-${option.id}`}
                        onClick={() => !props.disableUserActions && onRemoveOptionClick(option.id)}
                        qesType="secondary-danger"
                        title="Delete"
                      >
                          <QesIcon iconName={QesIcons.TrashV1} description="Delete" />
                      </QESButton>
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            testId={`access-reports-admin__manage-options--name-input-${option.id}`}
            errorText={optionNameBeingEditedValidationText}
            disabled={props.disableUserActions}
            hideLabel={true}
            isValid={!optionNameBeingEditedValidationText}
            label={optionNameBeingEdited === option.id ?
              optionNameBeingEditedValue :
              option.displayName ? option.displayName : option.name
            }
            readOnly={optionNameBeingEdited !== option.id}
            onChange={(event) => onOptionNameBeingEditedChange(event)}
            onKeyPress={(event) => onOptionNameBeingEditedKeyPress(event, optionNameBeingEditedValidationText, option.id)}
            unrequiredValidation={true}
            value={optionNameBeingEdited === option.id ?
              optionNameBeingEditedValue :
              option.displayName ? option.displayName : option.name
            }
          />

          {(!!connectedOptionsWarningElement && connectedOptionsWarningOptionId === option.id) ?
            connectedOptionsWarningElement :
            null
          }

          {!!props.buildAdditionalIndividualProperty &&
            props.buildAdditionalIndividualProperty(option)
          }
        </div>
      )
    });

    return (
      <div className="row">
        <div className="col-12">
          <DragAndDropReordering
            draggable={draggable}
            items={elements}
            onDragAndDropChange={onDragAndDropChange}
            onDragEnd={onDragEnd}
          />
        </div>
      </div>
    );
  }

  const onAddOptionClick = (): void => {
    const newOptions: AccessReportsWizardOption[] = cloneDeep(options);
    let maxId = 0;
    if (!!newOptions && newOptions.length) {
      maxId = Math.max(...newOptions.map((o) => o.id)) + 1;
    }

    const newOption: AccessReportsWizardOption = {
      defaultProducts: [],
      displayName: newOptionName,
      hiddenProducts: [],
      id: maxId,
      name: newOptionName,
    };

    newOptions.push(newOption);

    setConnectedOptionsWarningOptionId(null);
    setNewOptionName("");
    setOptions(newOptions);
  }

  const onEditOptionNameClick = (
    option: AccessReportsWizardOption
  ): void => {
    event.preventDefault();

    setOptionNameBeingEdited(option.id);
    setOptionNameBeingEditedValue(option.name);
  }

  const onOptionNameBeingEditedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setOptionNameBeingEditedValue(event.target.value);
  }

  const onOptionNameBeingEditedKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    optionNameBeingEditedTooltipText: string,
    optionId: number
  ): void => {
    // allow for the user to save the option currently being edited
    // on enter key press if valid
    if (event.key === "Enter" && !optionNameBeingEditedTooltipText) {
      event.preventDefault();
      onSaveOptionNameClick(optionId)
    }
  }

  const onOptionNameInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setNewOptionName(event.target.value);
  }

  const onRemoveOptionClick = (
    optionId: number,
  ): void => {
    if (!!props.getOptionConnections) {
      // determine if an option has any connected options prior to deletion;
      // if connected options do exist, display a warning
      const newConnectedOptionsWarningElement: JSX.Element = props.getOptionConnections(optionId);

      if (!!newConnectedOptionsWarningElement) {
        setConnectedOptionsWarningElement(newConnectedOptionsWarningElement);
        setConnectedOptionsWarningOptionId(optionId);
      } else {
        const optionsClone: AccessReportsWizardOption[] = cloneDeep(options);
        const newOptions: AccessReportsWizardOption[] = optionsClone.filter(x => x.id !== optionId);

        setOptions(newOptions);
      }
    } else {
      const optionsClone: AccessReportsWizardOption[] = cloneDeep(options);
      const newOptions: AccessReportsWizardOption[] = optionsClone.filter(x => x.id !== optionId);

      setOptions(newOptions);
    }
  }

  const onSaveOptionNameClick = (
    optionId: number,
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    const newOptions: AccessReportsWizardOption[] = cloneDeep(options);
    const option: AccessReportsWizardOption = newOptions.find(x => x.id === optionId);
    if (!!option) {
      option.displayName = optionNameBeingEditedValue;
      option.name = optionNameBeingEditedValue;

      setOptionNameBeingEdited(null);
      setOptionNameBeingEditedValue("");
      setOptions(newOptions);
    }
  }

  const initializeOptions = (): void => {
    // the direct options prop array is not modified
    // in case the user wishes to "cancel" any changes made
    setOptions(cloneDeep(props.options));
  }

  return (
    <React.Fragment>
      {!props.disableAddingNewOptions &&
        getAddOptionSection()
      }

      {!!props.additionalProperties &&
        props.additionalProperties
      }

      {options.length > 0 &&
        getOptionsList()
      }

      <hr />
      <FlyoutFooter
        submitButton={
          <QESButton
            className="ms-3"
            disabled={(props.requireMinimumOne && options.length < 1) || (
              !!props.savedOptions ?
                isEqual(props.savedOptions, options) :
                isEqual(props.options, options)
            ) || optionNameBeingEdited !== null ||
              props.disableUserActions
            }
            onClick={() => props.onSaveOptionsClick(options)}
            qesTestId="access-reports-admin__manage-options--save"
            qesType="primary"
          >
            Save
          </QESButton>
        }
      />
    </React.Fragment>
  );
}

export { ManageOptions };
